import { useNavigate, useParams } from "react-router-dom";
import {
  selectRecipeById,
  selectIngredientsByRecipeId,
  selectMethodStepsByRecipeId,
  updateRecipe,
  updateIngredients,
  updateMethodSteps,
  useT,
  useAppDispatch,
  useAppSelector,
  type Ingredient,
  type MethodStep,
  type Recipe,
  type RootState,
} from "@reciperoll/core";
import { Link } from "react-router-dom";
import { BookOpen } from "react-feather";
import { useRef, useState } from "react";
import { RecipeForm, type RecipeFormHandle } from "../components/RecipeForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { NotFoundView } from "../components/NotFoundView";
import { ingredientsToRows, methodStepsToRows } from "../utils/recipeFormRows";

export default function EditRecipe() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Confirm discard before leaving a touched form, using the form's isDirty handle.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const formRef = useRef<RecipeFormHandle>(null);

  const recipe = useAppSelector((s: RootState) => selectRecipeById(s, id ?? ""));
  const ingredients = useAppSelector((s) => selectIngredientsByRecipeId(s, id ?? ""));
  const methodSteps = useAppSelector((s) => selectMethodStepsByRecipeId(s, id ?? ""));

  if (!recipe) {
    return (
      <NotFoundView
        icon={<BookOpen className="w-8 h-8 text-terracotta" />}
        title={t("recipe.notFoundTitle")}
        message={t("recipe.notFoundMessage")}
        actions={
          <Link
            to="/recipes"
            className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2.5 text-sm font-bold text-white"
          >
            {t("recipe.browseRecipes")}
          </Link>
        }
      />
    );
  }
  const loaded = recipe;

  // Return to the recipe we came from (Edit was pushed on top of it) by going
  // *back*, rather than pushing a new recipe entry — otherwise the recipe's Back
  // button would land here, on the edit page. Fall back to the recipe route when
  // there's no history to pop (deep link / hard refresh on the edit URL).
  function exitToRecipe() {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(`/recipe/${loaded.id}`, { replace: true });
  }

  function handleSave(updated: Recipe, ings: Ingredient[], steps: MethodStep[]) {
    // Stamp contentEditedAt here (the edit-form save path) so it reflects genuine
    // content edits only — favourite/rating/cooked use their own actions.
    dispatch(updateRecipe({ ...updated, contentEditedAt: new Date().toISOString() }));
    dispatch(updateIngredients({ recipeId: loaded.id, ingredients: ings }));
    dispatch(updateMethodSteps({ recipeId: loaded.id, methodSteps: steps }));
    exitToRecipe();
  }

  function handleCancel() {
    if (formRef.current?.isDirty()) setConfirmCancel(true);
    else exitToRecipe();
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
      <RecipeForm
        ref={formRef}
        recipeId={loaded.id}
        mode="edit"
        initialRecipe={loaded}
        initialIngredients={ingredientsToRows(ingredients)}
        initialMethod={methodStepsToRows(methodSteps)}
        onSubmit={handleSave}
        onCancel={handleCancel}
      />

      {confirmCancel && (
        <ConfirmDialog
          title={t("addRecipe.discardTitle")}
          message={t("addRecipe.discardMessage")}
          confirmLabel={t("addRecipe.discard")}
          cancelLabel={t("addRecipe.keepEditing")}
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={() => {
            setConfirmCancel(false);
            exitToRecipe();
          }}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}
